import { ContentCopy, Download, MoreTime, OpenInNew, Stop } from "@mui/icons-material";
import { Button, ButtonGroup, Sheet, Snackbar, Stack, Typography } from "@mui/joy";
import { isPast } from "date-fns";
import { format } from "date-fns-tz";
import React from "react";

import { useCompetitionQuery, useUpdateCompetitionMutation } from "../../hooks/competitions";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "../../hooks/venues";

export default function CompetitionTools({ competitionUuid }: { competitionUuid: string }) {
  const venueUuid = useCurrentVenueUuid();
  const competitionQuery = useCompetitionQuery(competitionUuid, venueUuid);
  const { data: venue } = useCurrentVenueQuery();
  const competition = competitionQuery.data;

  const updateCompetitionMutation = useUpdateCompetitionMutation(
    competitionUuid,
    venueUuid,
    competition?.league?.uuid,
  );

  const [showMutationSnackbar, setShowMutationSnackbar] = React.useState(false);

  const manualEntryUrl = `https://api.griptonite.io/competitions/${competitionUuid}/entries/form.html`;

  const onCopyManualEntryLinkClick = () => {
    navigator.clipboard.writeText(manualEntryUrl);
  };

  const onAdd15Mins = () => {
    if (!competition) {
      return;
    }
    const newEndTime = Math.max(
      new Date(competition?.endTime).getTime() + 15 * 60 * 1000,
      Date.now() + 15 * 60 * 1000,
    );
    updateCompetitionMutation.mutate(
      {
        endTime: new Date(newEndTime).toISOString(),
      },
      {
        onSettled: () => {
          setShowMutationSnackbar(true);
        },
      },
    );
  };

  const onEndNow = () => {
    updateCompetitionMutation.mutate(
      {
        endTime: "NOW",
      },
      {
        onSettled: () => {
          setShowMutationSnackbar(true);
        },
      },
    );
  };

  const canEndNow = competition && !isPast(competition.endTime);

  return (
    <Stack
      sx={{
        backgroundColor: "neutral.200",
        padding: 2,
        flex: 1,
        overflow: "auto",
      }}
      spacing={2}
    >
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          Entry QR code
        </Typography>
        <Typography level="body-sm">
          Download a printable QR code for competitors to scan when they arrive at the venue.
        </Typography>
        <ButtonGroup sx={{ marginTop: 1 }} spacing={1}>
          <Button
            component={"a"}
            color="primary"
            variant="solid"
            startDecorator={<Download />}
            href={`https://api.griptonite.io/competitions/${competitionUuid}/entry-qrcodes/${venueUuid}.png`}
            target="_blank"
          >
            Download QR code
          </Button>
        </ButtonGroup>
      </Sheet>
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          Simple web scorecard
        </Typography>
        <Typography level="body-sm">
          Use this for competitors who cannot use the app. This page is also printable
        </Typography>
        <ButtonGroup sx={{ marginTop: 1 }} spacing={1}>
          <Button
            color="primary"
            variant="solid"
            startDecorator={<Download />}
            component="a"
            target="_blank"
            href={`https://api.griptonite.io/competitions/${competitionUuid}/manual-entry-qrcode.png`}
          >
            Download QR code
          </Button>
          <Button startDecorator={<ContentCopy />} onClick={onCopyManualEntryLinkClick}>
            Copy link
          </Button>
          <Button
            startDecorator={<OpenInNew />}
            component="a"
            target="_blank"
            href={manualEntryUrl}
          >
            Open in new tab
          </Button>
        </ButtonGroup>
      </Sheet>
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          End time
        </Typography>
        <Typography level="body-sm">
          Extend the competition by 15 minutes or end it early
        </Typography>
        <ButtonGroup sx={{ marginTop: 1 }} spacing={1}>
          <Button
            color="primary"
            variant="solid"
            startDecorator={<MoreTime />}
            onClick={onAdd15Mins}
            loading={
              updateCompetitionMutation.isPending &&
              updateCompetitionMutation.variables.endTime !== "NOW"
            }
            loadingPosition="start"
            disabled={updateCompetitionMutation.isPending}
          >
            Add 15 minutes
          </Button>
          <Button
            color="danger"
            variant="outlined"
            // hack to fix mui separator bug
            sx={
              !updateCompetitionMutation.isPending && canEndNow
                ? {
                    borderLeftColor: "var(--joy-palette-danger-outlinedBorder)!important",
                  }
                : {}
            }
            startDecorator={<Stop />}
            onClick={onEndNow}
            loading={
              updateCompetitionMutation.isPending &&
              updateCompetitionMutation.variables.endTime === "NOW"
            }
            loadingPosition="start"
            disabled={updateCompetitionMutation.isPending || !canEndNow}
          >
            End Now
          </Button>
        </ButtonGroup>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={showMutationSnackbar}
          onClose={() => setShowMutationSnackbar(false)}
          color={updateCompetitionMutation.isSuccess ? "success" : "danger"}
          autoHideDuration={10000}
          endDecorator={
            <Button
              onClick={() => setShowMutationSnackbar(false)}
              size="sm"
              variant="soft"
              color={updateCompetitionMutation.isSuccess ? "success" : "danger"}
            >
              Dismiss
            </Button>
          }
        >
          {competition && updateCompetitionMutation.isSuccess ? (
            <>
              Competition end time is now{" "}
              {format(competition.endTime, "HH:mm", {
                timeZone: venue?.timezone,
              })}
            </>
          ) : null}
          {updateCompetitionMutation.isError
            ? "End time update failed. Please try again or contact support."
            : null}
        </Snackbar>
      </Sheet>
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          Event on Griptonite website
        </Typography>
        <Typography level="body-sm">
          When you publish the competition, a page is automatically created on the Griptonite
          website to advertise the competition and where climbers can see the leaderboard and
          results.
        </Typography>
        <Button
          disabled={competition?.isHidden}
          sx={{ marginTop: 1 }}
          component="a"
          target="_blank"
          href={`https://griptonite.io/events/${competitionUuid}`}
          variant="solid"
          startDecorator={<OpenInNew />}
        >
          {competition?.isHidden ? "Publish to view the page" : "View competition page"}
        </Button>
      </Sheet>
    </Stack>
  );
}
